import React from "react";
import ProgramTemplate from "@/components/programs/ProgramTemplate";

const GuidedImplementation = () => (
  <ProgramTemplate
    pageKey="guided-implementation"
    breadcrumbLabel="Guided Implementation"
    overline="Program 02 · Flagship · Guided Implementation"
    headline="The methodology,"
    headlineAccent="personally implemented."
    audience="For individuals ready to apply the framework directly to their own physiology"
    lead="Guided Implementation is the flagship individual pathway — a rigorous, personalized application of Adaptive Physiology™ with structured coaching, ongoing assessment, and adaptive prescription across the full four-movement loop."
    loopHighlight="adaptation"
    whyExists={{
      title: "Some contexts require full implementation, not just introduction.",
      paragraphs: [
        "Learning the framework matters. Applying it — precisely, week after week, to a real nervous system with real load and real history — matters more. Guided Implementation is where the methodology is delivered in that fuller form.",
        "This is the pathway for people whose situation calls for personalized assessment, individualized prescription, disciplined reinforcement, and structured reassessment — the entire loop, delivered around one person.",
      ],
    }}
    howApplied={{
      title: "The full loop, delivered around one nervous system.",
      intro:
        "Everything the framework prescribes is applied here — with the calibration, cadence, and accountability that individualized implementation requires.",
      steps: [
        {
          title: "Comprehensive assessment",
          body: "HRV, autonomic tone, sleep architecture, current load, movement capacity, and behavioral markers captured together as the true starting signal.",
        },
        {
          title: "Personalized physiological strategy",
          body: "Precisely dosed exercise, movement, environment, and lifestyle inputs matched to the current state — not to a generic template.",
        },
        {
          title: "Structured reinforcement",
          body: "Weekly cadence, accountability, and behavioral conditioning ensure signal delivery — the condition under which the nervous system adapts.",
        },
        {
          title: "Adaptive reassessment",
          body: "Markers are re-measured at defined intervals; prescription adapts to the new baseline. The same protocol never runs twice against a changing system.",
        },
      ],
    }}
    experience={{
      title: "A working relationship with your own physiology.",
      intro:
        "Guided Implementation cultivates the capacities that make lasting behavior change physiologically possible — no outcome guarantees, only honest reporting of what the loop tends to build.",
      capacities: [
        { label: "Autonomic regulation", body: "Room in the system to receive stress and return to baseline." },
        { label: "Recovery velocity", body: "Faster return from load — physical, emotional, or social." },
        { label: "Behavioral flexibility", body: "Distance between trigger and response, where choice becomes possible." },
        { label: "Consistency capacity", body: "The physiological ability to run structure without collapsing it." },
        { label: "Adaptability under load", body: "New responses becoming available in the situations that matter." },
        { label: "Long-term self-regulation", body: "Independence from the program itself — the loop internalized." },
      ],
    }}
    designedFor={{
      title: "For those ready for the full protocol.",
      groups: [
        { title: "Individuals in recovery", body: "Retraining a nervous system that has adapted to substance-mediated regulation." },
        { title: "Adults in chronic stress or burnout", body: "Physiology stuck in sustained activation, needing more than rest." },
        { title: "Anxiety-pattern retrainers", body: "For patterns that fire before thought — the state, not the story, is the target." },
        { title: "Long-term habit changers", body: "People done with temporary motivation and ready to change the physiology itself." },
        { title: "High performers", body: "Athletes and professionals whose ceiling is set by autonomic capacity." },
        { title: "Trauma-adaptation participants", body: "Delivered alongside — not instead of — existing clinical care." },
      ],
    }}
    nextSteps={{
      primary: {
        label: "Apply to Guided Implementation",
        desc: "Applications are reviewed individually. Expect a response within three business days.",
        to: "/apply",
      },
      secondary: {
        label: "Start with Foundations",
        desc: "If understanding the framework first is a better fit for your context.",
        to: "/programs/foundations",
      },
    }}
  />
);
export default GuidedImplementation;
